import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseOptions,
} from "firebase/app";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

/**
 * Used when VITE_FIREBASE_* are not set at build time (common for local `vite build` without .env).
 * Prefer setting env in `.env` / CI — never `throw` at import time (breaks the whole app with a white screen).
 * Web app + project defaults for saving-b35f3; override via env in production.
 */
const FIREBASE_CONFIG_FALLBACK: Required<
  Pick<
    FirebaseOptions,
    | "apiKey"
    | "authDomain"
    | "projectId"
    | "storageBucket"
    | "messagingSenderId"
    | "appId"
  >
> = {
  apiKey: "AIzaSyAmJl2sK5HG-aIc6T8M6xdqV991mAeQ7n4",
  authDomain: "couponsapp.netlify.app",
  projectId: "saving-b35f3",
  storageBucket: "saving-b35f3.firebasestorage.app",
  messagingSenderId: "626636527932",
  appId: "1:626636527932:web:4f387aba70b7e8753fa639",
};

const requiredFirebaseEnvKeys = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
] as const;

const missingFirebaseEnvKeys = requiredFirebaseEnvKeys.filter(
  (key) => !import.meta.env[key],
);

if (import.meta.env.DEV && missingFirebaseEnvKeys.length > 0) {
  console.warn(
    "[Firebase] Some VITE_FIREBASE_* vars are missing; using built-in project defaults. Set a `.env` file for non-default projects.",
  );
}

const envOr = (
  key: (typeof requiredFirebaseEnvKeys)[number],
  fallback: string,
) => {
  const v = import.meta.env[key] as string | undefined;
  return v && String(v).trim() !== "" ? v : fallback;
};

const firebaseConfig: Required<
  Pick<
    FirebaseOptions,
    | "apiKey"
    | "authDomain"
    | "projectId"
    | "storageBucket"
    | "messagingSenderId"
    | "appId"
  >
> = {
  apiKey: envOr("VITE_FIREBASE_API_KEY", FIREBASE_CONFIG_FALLBACK.apiKey),
  authDomain: envOr(
    "VITE_FIREBASE_AUTH_DOMAIN",
    FIREBASE_CONFIG_FALLBACK.authDomain,
  ),
  projectId: envOr(
    "VITE_FIREBASE_PROJECT_ID",
    FIREBASE_CONFIG_FALLBACK.projectId,
  ),
  storageBucket: envOr(
    "VITE_FIREBASE_STORAGE_BUCKET",
    FIREBASE_CONFIG_FALLBACK.storageBucket,
  ),
  messagingSenderId: envOr(
    "VITE_FIREBASE_MESSAGING_SENDER_ID",
    FIREBASE_CONFIG_FALLBACK.messagingSenderId,
  ),
  appId: envOr("VITE_FIREBASE_APP_ID", FIREBASE_CONFIG_FALLBACK.appId),
};

export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const isNative = Capacitor.isNativePlatform();
const webAppCheckSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY;

if (!isNative && webAppCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(webAppCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (import.meta.env.DEV) {
  if (isNative) {
    // In Capacitor native flows, attestation is handled by native Firebase SDK setup (Play Integrity).
    console.info(
      "[Firebase] Native platform detected, skipping web App Check provider.",
    );
  } else {
    console.info(
      "[Firebase] Web App Check is disabled because VITE_RECAPTCHA_ENTERPRISE_SITE_KEY is missing.",
    );
  }
}
// Use indexedDB persistence on native for better compatibility with @capacitor-firebase/authentication
const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    })
  : getAuth(app);

// Arabic SMS for phone auth
auth.languageCode = "ar";

export { auth };
export const db = getFirestore(app);

/** Callable functions region — must match Cloud Functions deployment region. */
export const functions = getFunctions(
  app,
  import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION ||
    import.meta.env.VITE_FUNCTIONS_REGION ||
    "us-central1",
);

// Helper to check if running on native platform
export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();
